import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { supportService } from '../../services/support.service'

import { buildOutreachRequestEstimates, calculateOutreachResources, unlockOutreachChannels } from '../../utils/outreachResources'

interface SupportRequestModalProps {
  isOpen: boolean
  onClose: () => void
  userEmail?: string | null
  userName?: string | null
  impersonatedEmail?: string | null
  clientInfo?: { id: string; name: string; email: string; hasOutreachModule?: boolean } | null
  mode?: 'standard' | 'unlock_outreach' | 'contract_module'
  /** contract_module: the module(s) the user wants activated. */
  requestedModules?: string[]
}

export default function SupportRequestModal({
  isOpen,
  onClose,
  userEmail,
  userName,
  impersonatedEmail,
  clientInfo,
  mode = 'standard',
  requestedModules = [],
}: SupportRequestModalProps) {
  const isUnlockOutreachMode = mode === 'unlock_outreach'
  const isContractModuleMode = mode === 'contract_module'
  const hasOutreachModule = Boolean(clientInfo?.hasOutreachModule)
  const outreachRequestTitle = hasOutreachModule ? 'Upgrade Outreach Resources' : 'Contract Sales Intelligence'
  const outreachRequestHeading = hasOutreachModule ? 'Request More Outreach Capacity' : 'Set Up Your Outreach Engine'
  const outreachRequestSummary = hasOutreachModule
    ? 'Tell us your weekly target and preferred channels. Our team will review the extra setup needed to expand your Outreach capacity.'
    : 'Sales Intelligence runs your Outreach engine — sequences, follow-ups and replies. Tell us your weekly target and preferred channels so we can provision the sending infrastructure for your account.'
  const outreachRequestActionLabel = hasOutreachModule ? 'Send Upgrade Request' : 'Send Request'
  const moduleListLabel = requestedModules.length > 0 ? requestedModules.join(', ') : 'the selected module'
  const contractModuleTitle = 'Contract Module'
  const contractModuleHeading = 'Request Module Activation'
  const contractModuleSummary = `Module activation is handled by our team. Add any useful context and we'll set up ${moduleListLabel} for your account.`
  const contractModuleActionLabel = 'Send Activation Request'
  const [requestType, setRequestType] = useState<'bug' | 'contact'>('bug')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [weeklyLeads, setWeeklyLeads] = useState('')
  const [selectedChannels, setSelectedChannels] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitState, setSubmitState] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  useEffect(() => {
    if (!isOpen) {
      setRequestType('bug')
      setSubject('')
      setMessage('')
      setWeeklyLeads('')
      setSelectedChannels([])
      setSubmitState(null)
      setIsSubmitting(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, isSubmitting, onClose])

  if (!isOpen || typeof document === 'undefined') return null

  const { weeklyLeadTarget, dailyLeadTarget, resourceEstimates } = calculateOutreachResources(weeklyLeads, selectedChannels)
  const hasWeeklyLeadsValue = weeklyLeadTarget > 0

  const internalEstimates = isUnlockOutreachMode
    ? buildOutreachRequestEstimates(weeklyLeads, selectedChannels, hasOutreachModule)
    : null

  const plainLanguageSummary = hasWeeklyLeadsValue && selectedChannels.length > 0
    ? `We’ll prepare the Outreach setup needed to support ${weeklyLeadTarget.toLocaleString()} weekly leads across ${selectedChannels.join(', ')}.`
    : 'We’ll prepare the Outreach setup needed to support your weekly target across the selected channels.'

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isUnlockOutreachMode) {
      if (!weeklyLeads.trim()) {
        setSubmitState({ type: 'error', message: 'Please tell us how many weekly leads you want to reach.' })
        return
      }
      if (selectedChannels.length === 0) {
        setSubmitState({ type: 'error', message: 'Please select at least one outreach channel.' })
        return
      }
    } else if (isContractModuleMode) {
      // Message optional — the request carries the module list regardless.
    } else if (!subject.trim() || !message.trim()) {
      setSubmitState({ type: 'error', message: 'Please add a subject and message.' })
      return
    }

    setIsSubmitting(true)
    setSubmitState(null)

    const generatedUnlockSubject = `${hasOutreachModule ? 'Upgrade Outreach Resources' : 'Unlock Outreach Request'}${clientInfo?.name ? ` - ${clientInfo.name}` : ''}`
    const generatedUnlockMessage = [
      hasOutreachModule
        ? 'Client requested additional Outreach capacity.'
        : 'Client requested Outreach module activation.',
      `Weekly leads target: ${weeklyLeads.trim()}`,
      `Channels: ${selectedChannels.join(', ')}`,
      `Summary: ${plainLanguageSummary}`,
    ].join('\n')
    const generatedContractSubject = `Module Activation Request - ${moduleListLabel}${clientInfo?.name ? ` - ${clientInfo.name}` : ''}`
    const generatedContractMessage = [
      `Client requested activation of: ${moduleListLabel}`,
      message.trim() ? `Context: ${message.trim()}` : 'No additional context provided.',
    ].join('\n')

    const result = await supportService.sendRequest({
      type: isContractModuleMode ? 'contract_module' : isUnlockOutreachMode ? 'unlock_outreach' : requestType,
      subject: isContractModuleMode
        ? generatedContractSubject
        : isUnlockOutreachMode
          ? generatedUnlockSubject
          : subject.trim(),
      message: isContractModuleMode
        ? generatedContractMessage
        : isUnlockOutreachMode
          ? generatedUnlockMessage
          : message.trim(),
      pageUrl: window.location.href,
      weeklyLeads: isUnlockOutreachMode ? weeklyLeads.trim() : undefined,
      channels: isUnlockOutreachMode ? selectedChannels : undefined,
      requestedModules: isContractModuleMode ? requestedModules : undefined,
      internalEstimates,
      userEmail,
      userName,
      impersonatedEmail,
      clientInfo,
    })

    if (result.success) {
      setSubmitState({
        type: 'success',
        message: result.message || 'Your setup request has been sent. Our team will review the required resources and get back to you.',
      })
      setSubject('')
      setMessage('')
      setWeeklyLeads('')
      setSelectedChannels([])
    } else {
      setSubmitState({
        type: 'error',
        message: 'We could not send your message right now. Please try again.',
      })
    }

    setIsSubmitting(false)
  }

  return createPortal(
    <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/75 p-4 backdrop-blur-md">
      <button
        type="button"
        aria-label="Close support modal"
        className="absolute inset-0"
        onClick={() => {
          if (!isSubmitting) onClose()
        }}
      />

      <div className={`relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-[32px] border border-white/10 bg-[linear-gradient(160deg,rgba(16,16,18,0.98),rgba(8,8,10,0.96))] shadow-[0_34px_100px_rgba(0,0,0,0.5)] ${isUnlockOutreachMode || isContractModuleMode ? 'max-w-2xl' : 'max-w-3xl'}`}>
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(82,96,255,0.18),transparent_24%),radial-gradient(circle_at_bottom_right,rgba(34,211,238,0.08),transparent_20%)]" />

        <div className="relative border-b border-white/8 px-6 py-6 md:px-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/18 bg-cyan-400/8 px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em] text-cyan-100/90">
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-4l-4 4v-4z" />
            </svg>
            {isUnlockOutreachMode
              ? outreachRequestTitle
              : isContractModuleMode
                ? contractModuleTitle
                : 'Report Bug Or Contact'}
          </div>
          <h2 className="mt-4 text-3xl font-black tracking-tight text-white">
            {isUnlockOutreachMode
              ? outreachRequestHeading
              : isContractModuleMode
                ? contractModuleHeading
                : 'Need help from the team?'}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-400">
            {isUnlockOutreachMode
              ? outreachRequestSummary
              : isContractModuleMode
                ? contractModuleSummary
                : 'Send us a bug report or a support message with the page context attached.'}
          </p>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/60 transition-all hover:border-white/20 hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="relative min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-6 md:px-8 md:py-7">
          {!isUnlockOutreachMode && !isContractModuleMode && (
            <div className="grid gap-4 md:grid-cols-2">
              {[
                {
                  value: 'bug' as const,
                  title: 'Report a bug',
                  description: 'Technical issue, broken flow, missing data, or something behaving incorrectly.',
                },
                {
                  value: 'contact' as const,
                  title: 'Contact the team',
                  description: 'General help, product questions, or anything that needs a human response.',
                },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setRequestType(option.value)}
                  className={`rounded-[24px] border p-4 text-left transition-all ${
                    requestType === option.value
                      ? 'border-[#5260FF]/38 bg-[linear-gradient(135deg,rgba(82,96,255,0.16),rgba(11,17,24,0.96))] shadow-[0_18px_38px_rgba(82,96,255,0.14)]'
                      : 'border-white/8 bg-white/[0.03] hover:border-white/16 hover:bg-white/[0.05]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className={`mt-0.5 flex h-11 w-11 items-center justify-center rounded-2xl border ${
                      requestType === option.value
                        ? 'border-cyan-300/24 bg-cyan-400/10 text-cyan-100'
                        : 'border-white/10 bg-black/20 text-gray-400'
                    }`}>
                      {option.value === 'bug' ? (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-4l-4 4v-4z" />
                        </svg>
                      )}
                    </span>
                    <div>
                      <div className="text-sm font-bold text-white">{option.title}</div>
                      <p className="mt-1 text-xs leading-5 text-gray-500">{option.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className={`grid gap-5 ${isUnlockOutreachMode || isContractModuleMode ? '' : 'lg:grid-cols-[1.4fr_0.8fr]'}`}>
            <div className="space-y-5 rounded-[28px] border border-white/8 bg-[linear-gradient(145deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] p-5 md:p-6">
              {isContractModuleMode ? (
                <>
                  <div className="rounded-[24px] border border-[#5260FF]/18 bg-[linear-gradient(145deg,rgba(82,96,255,0.12),rgba(9,10,15,0.92))] p-5">
                    <div className="text-[11px] font-black uppercase tracking-[0.2em] text-cyan-100/80">
                      Requested Module{requestedModules.length === 1 ? '' : 's'}
                    </div>
                    <p className="mt-3 text-sm font-bold leading-6 text-cyan-50/90">
                      {moduleListLabel}
                    </p>
                    <p className="mt-2 text-xs leading-5 text-cyan-50/60">
                      Our team activates modules for your account — you'll be notified once it's live.
                    </p>
                  </div>

                  <div>
                    <label className="mb-2 block text-[11px] font-black uppercase tracking-[0.2em] text-gray-400">
                      Context (optional)
                    </label>
                    <textarea
                      value={message}
                      onChange={(event) => setMessage(event.target.value)}
                      placeholder="Anything useful about your use case, expected volume, or timeline."
                      className="min-h-[120px] w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm leading-6 text-white outline-none transition-colors placeholder:text-gray-500 focus:border-[#5260FF]/50"
                      disabled={isSubmitting}
                    />
                  </div>
                </>
              ) : isUnlockOutreachMode ? (
                <>
                  <div>
                    <label className="mb-2 block text-[11px] font-black uppercase tracking-[0.2em] text-gray-400">
                      Weekly Leads Target
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={weeklyLeads}
                      onChange={(event) => setWeeklyLeads(event.target.value)}
                      placeholder="Example: 250"
                      className="w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-gray-500 focus:border-[#5260FF]/50"
                      disabled={isSubmitting}
                    />
                    <p className="mt-2 text-xs leading-5 text-gray-500">
                      Share your target volume so our team can prepare the right Outreach setup for you.
                    </p>
                  </div>

                  <div>
                    <label className="mb-3 block text-[11px] font-black uppercase tracking-[0.2em] text-gray-400">
                      Channels
                    </label>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {unlockOutreachChannels.map((channel) => {
                        const selected = selectedChannels.includes(channel)
                        return (
                          <button
                            key={channel}
                            type="button"
                            disabled={isSubmitting}
                            onClick={() =>
                              setSelectedChannels((current) =>
                                selected ? current.filter((value) => value !== channel) : [...current, channel]
                              )
                            }
                            className={`rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition-all ${
                              selected
                                ? 'border-[#5260FF]/38 bg-[linear-gradient(135deg,rgba(82,96,255,0.16),rgba(11,17,24,0.96))] text-white shadow-[0_18px_38px_rgba(82,96,255,0.14)]'
                                : 'border-white/8 bg-white/[0.03] text-gray-300 hover:border-white/16 hover:bg-white/[0.05]'
                            }`}
                          >
                            {channel}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  <div className="rounded-[24px] border border-[#5260FF]/18 bg-[linear-gradient(145deg,rgba(82,96,255,0.12),rgba(9,10,15,0.92))] p-5">
                    <div className="text-[11px] font-black uppercase tracking-[0.2em] text-cyan-100/80">
                      Request Summary
                    </div>
                    <p className="mt-3 text-sm leading-6 text-cyan-50/88">
                      {plainLanguageSummary}
                    </p>
                    <p className="mt-3 text-xs leading-5 text-cyan-50/60">
                      We’ll review the required setup for your selected channels and come back with the next steps.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="mb-2 block text-[11px] font-black uppercase tracking-[0.2em] text-gray-400">
                      Subject
                    </label>
                    <input
                      value={subject}
                      onChange={(event) => setSubject(event.target.value)}
                      placeholder={requestType === 'bug' ? 'Example: Report download fails after enrichment' : 'Example: Need help with campaign setup'}
                      className="w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-gray-500 focus:border-[#5260FF]/50"
                      maxLength={140}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-[11px] font-black uppercase tracking-[0.2em] text-gray-400">
                      Message
                    </label>
                    <textarea
                      value={message}
                      onChange={(event) => setMessage(event.target.value)}
                      placeholder={requestType === 'bug' ? 'Tell us what happened, what you expected, and if possible the last action you took.' : 'Tell us how we can help you.'}
                      className="min-h-[190px] w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm leading-6 text-white outline-none transition-colors placeholder:text-gray-500 focus:border-[#5260FF]/50"
                      disabled={isSubmitting}
                    />
                  </div>
                </>
              )}
            </div>

            {!isUnlockOutreachMode && !isContractModuleMode && (
            <div className="space-y-4 rounded-[28px] border border-white/8 bg-[linear-gradient(145deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] p-5 md:p-6">
              <div>
                <div className="text-[11px] font-black uppercase tracking-[0.2em] text-gray-400">Session Context</div>
                <div className="mt-3 space-y-3 text-sm text-gray-300">
                  <div>
                    <div className="text-xs uppercase tracking-[0.14em] text-gray-500">Signed-in Email</div>
                    <div className="mt-1 break-all text-white">{userEmail || 'Unavailable'}</div>
                  </div>
                  {clientInfo?.name && (
                    <div>
                      <div className="text-xs uppercase tracking-[0.14em] text-gray-500">Client</div>
                      <div className="mt-1 text-white">{clientInfo.name}</div>
                    </div>
                  )}
                  {impersonatedEmail && (
                    <div>
                      <div className="text-xs uppercase tracking-[0.14em] text-gray-500">Impersonating</div>
                      <div className="mt-1 break-all text-amber-200">{impersonatedEmail}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-xs uppercase tracking-[0.14em] text-gray-500">Current Page</div>
                    <div className="mt-1 break-all text-white/80">{window.location.pathname}</div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-cyan-300/12 bg-cyan-400/6 p-4 text-xs leading-6 text-cyan-50/80">
                {isUnlockOutreachMode
                  ? `We attach your current page, account context, weekly target, channels, and resource estimate summary so the team can scope the ${hasOutreachModule ? 'upgrade' : 'setup'} faster.`
                  : 'We attach your current page, account context, and message so the team can understand the issue faster.'}
              </div>
            </div>
            )}
          </div>

          {submitState && (
            <div className={`rounded-2xl border px-4 py-3 text-sm ${
              submitState.type === 'success'
                ? 'border-emerald-400/18 bg-emerald-400/8 text-emerald-50/90'
                : 'border-red-400/18 bg-red-400/8 text-red-50/90'
            }`}>
              {submitState.message}
            </div>
          )}

          <div className="flex justify-end gap-3 border-t border-white/8 pt-5">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-2xl border border-white/8 bg-white/[0.03] px-5 py-2.5 text-sm font-semibold text-gray-400 transition-colors hover:border-white/14 hover:bg-white/[0.05] hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 rounded-2xl border border-[#5260FF]/30 bg-[#5260FF] px-5 py-2.5 text-sm font-black text-white shadow-[0_16px_32px_rgba(82,96,255,0.24)] transition-all hover:bg-[#4553f1] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isSubmitting ? (
                <>
                  <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4l16 8-16 8V4z" />
                  </svg>
                  {isUnlockOutreachMode
                    ? outreachRequestActionLabel
                    : isContractModuleMode
                      ? contractModuleActionLabel
                      : 'Send Message'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}
